<template>
  <section>
    <div :class="{ 'd-flex justify-content-between': is6MWT }">
      <div class="card mt-3 mb-2" :class="{ 'term-col-50 term-margin': is6MWT }">
        <div class="card-header card-header-light">
          <h4 class="card-header__title">{{ $t('src/components/training/trainingresults.5946') }}</h4>
        </div>
        <div class="card-body">
          <training-results-info
            :duration="trainingDurationInSec"
            :rhythmogram="rhythmogram"
            :step-rate="stepRate"
            :step-count="training.step_count"
            :hr-min="training.heart_rate_min"
            :hr-max="training.heart_rate_max"
            :borg-scale="training.borg_scale"
            :comment="training.comment"
            :is6MWT="is6MWT"
            :isActive="isActive"
          ></training-results-info>
        </div>
      </div>

      <div v-if="is6MWT" class="card mt-3 mb-2 term-col-50">
        <div class="card-header card-header-light">
          <h4 class="card-header__title">{{ $t('src/components/training/trainingresults.1190') }}</h4>
        </div>
        <div class="card-body">
          <training-results-6-m-w-t
            :blood-pressure-before="training.blood_pressure_before"
            :blood-pressure-after="training.blood_pressure_after"
            :saturation-before="training.saturation_before"
            :saturation-after="training.saturation_after"
            :distance="training.distance"
            :patient="patient"
          >
          </training-results-6-m-w-t>
        </div>
      </div>
    </div>

    <div v-if="hasRhythmogram" class="card mt-3 mb-2">
      <div class="card-header card-header-light">
        <h4 class="card-header__title d-flex justify-content-between align-items-center">
          <div>
            {{ $t('src/components/training/trainingresults.9521')
            }}<span class="font-weight-normal ml-2" v-if="hasRhythmogram">{{
              `(${$t('sessionsInfo.averageHeartRate_lower')}: ${averageHeartRate} ${$t('unit.bpm')}, ${$t(
                'sessionsInfo.respiratoryRate'
              )}: ${averageBreath}/${$t('general.min')})`
            }}</span>
          </div>
          <div v-if="isActive" class="dropleft">
            <span v-if="!this.connect"> {{ connectionStr }} </span>
            <svg-icon
              :name="nameIconConnect"
              class="svg-icon-without-hover mr-3"
              :height="22"
              :width="22"
            ></svg-icon>
          </div>
        </h4>
      </div>
      <div class="card-chart">
        <training-chart
          ref="trainingChart"
          :trainingType="training.training_type"
          :date-start="new Date(trainingStartDate)"
          :rhythmogram="rhythmogram"
          :steps="stepRate"
          :min="training.heart_rate_min"
          :max="training.heart_rate_max"
          :events="activeEventsList"
          :breath="breath"
          :is-active-training="isActive"
          :training-duration="trainingDurationInSec"
          :patient-max-hr="maxHr"
          @input="scrollChartEcg"
          @set:heartRate="setValue"
          :ecgRange="ecgRange"
        ></training-chart>
      </div>
    </div>

    <div v-if="hasECG" class="card mt-3 mb-2">
      <div class="card-chart card-chart-ecg" id="chart-ecg" :key="componentKey">
        <training-ecg
          ref="ecg1"
          :trainingId="training.id"
          :timeBegin="begin"
          :events="activeEventsList"
          :averageHR="averageHrForEcg"
          :duration="trainingDurationInSec"
          @set:hr="setHrForEcg"
          @set:scaleX="setScaleX"
          @set:scaleY="setScaleY"
          v-on="$listeners"
          @change:part="changePartECG"
          :key="1"
          @reload:ecg="reloadEcg"
        >
        </training-ecg>
      </div>
    </div>

    <div v-if="isActive && !isNeuroAdmin" class="card mt-3 mb-2">
      <div class="card-chart card-chart-ecg" :key="componentKey">
        <training-ecg-real-time :trainingId="training.id" :current-h-r="currentHR" @reload:ecg="reloadEcg">
        </training-ecg-real-time>
      </div>
    </div>

    <div v-if="hasEvents" class="card mt-3 mb-2">
      <div class="card-header card-header-light d-flex justify-content-between align-items-center">
        <h4 class="card-header__title">
          <span class="icon icon-pulce icon_danger mr-2"></span
          >{{ $t('src/components/training/trainingresults.5268', { '1': countEvents }) }}
          <div v-if="isManageCourse && !isActive" class="custom-control custom-switch ml-5">
            <input
              type="checkbox"
              class="custom-control-input"
              id="customSwitch"
              :checked="isEditable"
              @input="openEdit"
            />
            <label class="custom-control-label" for="customSwitch">{{ $t('button.edit') }}</label>
          </div>
        </h4>
        <base-dropdown position="left" class="dropdown-filter">
          <template #dropdownContent>
            <div class="dropdown-menu__body">
              <cardio-events-filter
                :exist-events="filterEvents"
                :selected-events="selectedEvents"
                :selected-importance="selectedImportance"
                @set:events="inputEvents"
                @input:importance="inputImportance"
                class="filter-multiple-column"
              ></cardio-events-filter>
            </div>
          </template>
          <template #buttonText>{{ $t('src/components/training/trainingresults.591') }}</template>
        </base-dropdown>
      </div>
      <div class="card-body">
        <table class="table table-sm table-hover table-cardio-events">
          <tbody v-if="activeEventsList">
            <training-event-row
              v-for="event in activeEventsList"
              :key="event.key"
              :event="event"
              :isEditable="isEditable"
              @input="scrollChartEcg"
              @selectEvent="selectEvent"
            ></training-event-row>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="hasGeoLocationPoints" class="card mt-3 mb-2">
      <route-map
        :geoLocationPoints="geoLocationPoints"
        :geoDistance="training.geo_filtered_distance"
      ></route-map>
    </div>
  </section>
</template>

<script>
import { status, TRAINING_TYPE } from '@/main.config.js'
import { eventsConfig, notShow } from '@/events.config.js'
import { trainingDate } from '@/mixins/training-date.js'
import { patientMaxHR } from '@/mixins/patient-max-hr'
import getAverageOfArray from '@/modules/average-of-array.js'
import { userRights } from '@/mixins/user-rights.js'
import http from '@/modules/http'
import { systemUnavailable } from '@/mixins/system-unavailable.js'

import('@/styles/components/table.less')

export default {
  name: 'TrainingResults',
  components: {
    TrainingResultsInfo: () => import('@/components/Training/TrainingResultsInfo.vue'),
    TrainingResults6MWT: () => import('@/components/Training/TrainingResults6MWT.vue'),
    TrainingChart: () => import('@/components/Training/TrainingChart.vue'),
    TrainingEventRow: () => import('@/components/Training/TrainingEventRow.vue'),
    CardioEventsFilter: () => import('@/components/Filters/CardioEventsFilter.vue'),
    BaseDropdown: () => import('@/base/BaseDropdown.vue'),
    TrainingEcg: () => import('@/components/Training/TrainingEcg.vue'),
    TrainingEcgRealTime: () => import('@/components/Training/TrainingEcgRealTime.vue'),
    SvgIcon: () => import('@/components/SvgIcon.vue'),
    RouteMap: () => import('@/components/RouteMap.vue')
  },
  mixins: [trainingDate, patientMaxHR, userRights, systemUnavailable],
  provide: function() {
    return {
      trainingStartDate: this.trainingStartDate,
      trainingEndDate: this.trainingEndDate,
      is6MWT: this.is6MWT
    }
  },
  props: {
    training: {
      type: Object,
      required: true
    },
    rhythmogram: {
      type: Object,
      required: false
    },
    events: {
      type: Array,
      required: false
    },
    stepRate: {
      type: Object,
      required: false
    },
    currentHR: {
      type: Number,
      required: false
    },
    breath: {
      type: Object,
      required: false
    },
    diffTimeComputerAndServer: {
      type: Number,
      require: true
    },
    geoLocationPoints: {
      type: Object,
      required: false
    }
  },
  data: () => ({
    timer: null,
    selectedEvents: [],
    selectedImportance: ['danger', 'warning', 'primary', 'secondary'],
    now: null,
    begin: 0,
    ecgRange: {},
    averageHR: null,
    averageBR: null,
    averageHrForEcg: null,
    isEditable: false,
    transformEvents: { restore: [], deleted: [] },
    scaleX: null,
    scaleY: null,
    connect: true,
    generalEventsList: null,
    existingUndeletedEventsList: null,
    deletedEventsList: null,
    componentKey: 0
  }),
  computed: {
    patient() {
      return this.training.course.patient
    },
    course() {
      return this.training.course
    },
    countEvents() {
      const events = this.activeEventsList
      if (!events) {
        return 0
      }
      return events.length
    },
    isActive() {
      return this.training.status === status.active
    },
    hasRhythmogram() {
      return this.rhythmogram && this.rhythmogram.timeValues.length > 0
    },
    hasBreath() {
      return this.breath && this.breath.timeValues.length > 0
    },
    hasEvents() {
      return this.activeEventsList !== null && this.activeEventsList.length
    },
    hasGeoLocationPoints() {
      return this.geoLocationPoints && this.geoLocationPoints.geoValues.length > 0
    },
    trainingStartDate() {
      return this.getTrainingMomentStartDate(this.training)
    },
    trainingEndDate() {
      return this.getTrainingMomentEndDate(this.training)
    },
    trainingDurationInSec() {
      let duration = 0

      if (this.isActive && this.trainingStartDate) {
        let now = this.now
        if (!now) {
          now = this.moment().local()
        }
        duration = now.diff(this.trainingStartDate, 'seconds') - this.diffTimeComputerAndServer
      } else if (this.trainingEndDate && this.trainingStartDate) {
        duration = this.getDoneTrainingDuration(this.training)
      }

      return duration
    },
    is6MWT() {
      return this.training.training_type === TRAINING_TYPE.sixmwt
    },
    hasECG() {
      return this.training.ecg_record && this.training.status == status.done
    },
    averageHeartRate() {
      return this.averageHR ? this.averageHR : '—'
    },
    averageBreath() {
      return this.averageBR ? this.averageBR : '—'
    },
    filterEvents() {
      return this.isEditable ? this.generalEventsList : this.existingUndeletedEventsList
    },
    connectionStr() {
      if (this.connect) {
        return this.$t('src/components/table-rows/trainingstablerow.online')
      } else {
        return this.$t('src/components/table-rows/trainingstablerow.offline')
      }
    },
    spanClass() {
      return this.connect ? 'connection-online' : 'connection-offline'
    },
    activeEventsList() {
      let list = this.isEditable ? this.generalEventsList : this.existingUndeletedEventsList
      if (this.selectedImportance?.length < 4) {
        list = list.filter(item => {
          return this.selectedImportance.includes(item.color)
        })
      }
      if (this.selectedEvents?.length > 0) {
        list = list.filter(item => {
          return this.selectedEvents.includes(item.type)
        })
      }
      return list
    },
    generalEvents() {
      let del_events = this.deletedEventsList
      let exist_events = this.existingUndeletedEventsList
      let res = exist_events.concat(del_events)
      return res.sort((a, b) => (a.begin > b.begin ? 1 : -1))
    },
    nameIconConnect() {
      return this.connect ? 'connection' : 'connection_off'
    }
  },
  watch: {
    events: {
      deep: true,
      handler() {
        this.existingUndeletedEventsList = this.changeArrayEvents(this.events)
        this.deletedEventsList = this.changeArrayEvents(JSON.parse(this.training.deleted_events), true)
        this.generalEventsList = this.generalEvents
      }
    },
    isEditable() {
      if (
        !this.isEditable &&
        (this.transformEvents.restore.length > 0 || this.transformEvents.deleted.length > 0)
      ) {
        this.transformationEventsList(JSON.stringify(this.transformEvents))
      } else {
        this.transformEvents = { restore: [], deleted: [] }
      }
    },
    isActive() {
      if (this.isActive) {
        this.updateNow()
      } else {
        this.timer = null
      }
    },
    rhythmogram: {
      deep: true,
      handler() {
        this.averageHR = getAverageOfArray(this.rhythmogram.hrValues)
        if (this.hasBreath) {
          this.averageBR = getAverageOfArray(this.breath.brValues)
        }
      }
    },
    trainingDurationInSec() {
      const diff = this.trainingDurationInSec - +localStorage.getItem(`lastDataTime${this.training.id}`)
      if (diff >= 40) {
        this.connect = false
      } else this.connect = true
    },
    existingUndeletedEventsList: {
      deep: true,
      handler() {
        this.$emit('set:localEvents', this.existingUndeletedEventsList)
      }
    }
  },
  created() {
    document.addEventListener('keydown', this.onKeyDown)
  },
  mounted() {
    this.now = this.moment().local()

    this.existingUndeletedEventsList = this.changeArrayEvents(this.events)
    this.deletedEventsList = this.changeArrayEvents(JSON.parse(this.training.deleted_events), true)
    this.generalEventsList = this.generalEvents

    if (this.isActive) {
      this.updateNow()
    }
    if (this.hasRhythmogram) {
      this.averageHR = getAverageOfArray(this.rhythmogram.hrValues)
    }
    if (this.hasBreath) {
      this.averageBR = getAverageOfArray(this.breath.brValues)
    }
  },
  methods: {
    reloadEcg() {
      this.componentKey += 1
    },
    updateNow() {
      this.timer = setInterval(() => {
        this.now = this.moment().local()
      }, 1000)
    },
    changeArrayEvents(eventsArray, isDeleted = false) {
      if (!eventsArray) {
        return []
      }
      let changedArray = eventsArray.reduce((filteredEvents, event, i) => {
        if (this.selectedEvents.length && !this.selectedEvents.includes(event.episodeType)) {
          return filteredEvents
        }

        let conf = eventsConfig[event.episodeType]
        if (!conf) {
          conf = {
            abbr: '?',
            text: this.$t('src/components/training/trainingresults.unknown'),
            duration: true,
            decimalNumbers: 0
          }
        }
        if (
          (!eventsArray[i - 1] ||
            eventsArray[i - 1].beginTime !== event.beginTime ||
            eventsArray[i - 1].episodeType !== event.episodeType) &&
          !notShow.includes(event.episodeType) &&
          this.selectedImportance.indexOf(eventsConfig[event.episodeType].color) !== -1
        ) {
          if (!event.endTime || event.beginTime <= event.endTime) {
            filteredEvents.push({
              key: `${event.episodeType}${event.value ? event.value : ''}${event.beginTime}`,
              type: event.episodeType,
              title: conf.text,
              abbr: conf.abbr,
              begin: event.beginTime,
              end: event.endTime,
              isDuration: conf.duration,
              color: conf.color,
              value: conf.value && event.value ? event.value : false,
              unit: conf.unit ? conf.unit : false,
              isDeleted: isDeleted
            })
          }
        }

        return filteredEvents
      }, [])
      return changedArray.sort((a, b) => (a.begin > b.begin ? 1 : -1))
    },
    inputEvents(selectedEvents) {
      this.selectedEvents = selectedEvents
    },
    inputImportance(selectedImportance) {
      this.selectedImportance = selectedImportance
    },
    scrollChartEcg(timeBegin) {
      this.begin = timeBegin
    },
    changePartECG(start, end) {
      this.ecgRange = { start: start, end: end }
    },
    setValue(heartRate, breath) {
      this.averageHR = heartRate
      this.averageBR = breath
    },
    setHrForEcg(from, to) {
      this.averageHrForEcg = this.findAverageSelectedGraph(from, to)
    },
    setScaleX(value) {
      this.scaleX = +value
    },
    setScaleY(value) {
      this.scaleY = +value
    },
    findAverageSelectedGraph(from, to) {
      let fromClosest = null
      let toClosest = null
      let fromClosestIdx = null
      let toClosestIdx = null
      this.rhythmogram.timeValues.forEach((item, i) => {
        if (fromClosest == null || Math.abs(item - from) < Math.abs(fromClosest - from)) {
          fromClosest = item
          fromClosestIdx = i
        } else if (toClosest == null || Math.abs(item - to) < Math.abs(toClosest - to)) {
          toClosest = item
          toClosestIdx = i
        }
      })

      const shortArray = this.rhythmogram.hrValues.slice(fromClosestIdx, toClosestIdx + 1)
      return shortArray.length > 0 ? getAverageOfArray(shortArray) : null
    },
    openEdit() {
      if (Number(localStorage.getItem('activeHospital'))) {
        this.isEditable = !this.isEditable
        this.selectedEvents = []
      } else {
        this.$emit('system:unavailable')
      }
    },
    transformationEventsList(event) {
      http.patch('/trainings/' + this.training.id, { mutable_events: event }).then(res => {
        this.existingUndeletedEventsList = this.changeArrayEvents(JSON.parse(res.data.events))
        this.deletedEventsList = this.changeArrayEvents(JSON.parse(res.data.deleted_events), true)
        this.generalEventsList = this.generalEvents
      })
    },
    selectEvent(event, bol) {
      if (bol) {
        if (this.transformEvents.restore.includes(event)) {
          const idx = this.transformEvents.restore.findIndex(ev => ev == event)
          this.transformEvents.restore.splice(idx, 1)
        } else {
          this.transformEvents.deleted.push(event)
        }
      } else {
        if (this.transformEvents.deleted.includes(event)) {
          const idx = this.transformEvents.deleted.findIndex(ev => ev == event)
          this.transformEvents.deleted.splice(idx, 1)
        } else {
          this.transformEvents.restore.push(event)
        }
      }
    }
  }
}
</script>
